import { DocumentData } from "firebase/firestore";
import { dateField, stringArrayField, stringField } from "./firestoreHelpers";
import { ActivityEventRecord, AdminUserRecord, CoachRecord, ShoutOutRecord } from "./types";

export type BookingRecord = {
  id: string;
  userId: string | null;
  coachUserId: string | null;
  providerUserId: string | null;
  status: string | null;
  updatedAt: Date | null;
};

export type PaymentRecord = {
  id: string;
  payerUserId: string | null;
  payeeUserId: string | null;
  customerUserId: string | null;
  providerUserId: string | null;
  status: string | null;
  grossAmountUSD: number | null;
  updatedAt: Date | null;
};

export function mapUserRecord(id: string, data: DocumentData): AdminUserRecord {
  return {
    id,
    email: stringField(data, "email"),
    displayName: stringField(data, "displayName"),
    username: stringField(data, "username"),
    role: stringField(data, "role") ?? stringField(data, "activeRole"),
    roles: stringArrayField(data, "roles"),
    goals: stringArrayField(data, "goals"),
    accountType: stringField(data, "accountType"),
    guardianApprovalStatus: stringField(data, "guardianApprovalStatus"),
    parentGuardianUserId: stringField(data, "parentGuardianUserId"),
    consentStatus: stringField(data, "consentStatus"),
    verificationStatus: stringField(data, "verificationStatus"),
    isSuspended: data.isSuspended === true,
    createdAt: dateField(data, "createdAt"),
    lastSeenAt: dateField(data, "lastSeenAt")
  };
}

export function mapActivityEventRecord(id: string, data: DocumentData): ActivityEventRecord {
  return {
    id,
    eventName: stringField(data, "eventName"),
    eventType: stringField(data, "eventType"),
    userId: stringField(data, "userId"),
    createdAt: dateField(data, "createdAt") ?? dateField(data, "timestamp")
  };
}

export function mapShoutOutRecord(id: string, data: DocumentData): ShoutOutRecord {
  return {
    id,
    senderUserId: stringField(data, "senderUserId"),
    recipientDancerUserId: stringField(data, "recipientDancerUserId"),
    senderDisplayNameSnapshot: stringField(data, "senderDisplayNameSnapshot"),
    topic: stringField(data, "topic"),
    privateNote: stringField(data, "privateNote"),
    visibility: stringField(data, "visibility"),
    createdAt: dateField(data, "createdAt")
  };
}

export function mapCoachRecord(id: string, data: DocumentData): CoachRecord {
  return {
    id,
    displayName: stringField(data, "displayName"),
    email: stringField(data, "email"),
    verificationStatus: stringField(data, "verificationStatus"),
    verificationType: stringField(data, "verificationType"),
    role: stringField(data, "role") ?? stringField(data, "activeRole"),
    roles: stringArrayField(data, "roles")
  };
}

export function mapBookingRecord(id: string, data: DocumentData): BookingRecord {
  return {
    id,
    userId: stringField(data, "userId") ?? stringField(data, "customerUserId"),
    coachUserId: stringField(data, "coachUserId"),
    providerUserId: stringField(data, "providerUserId"),
    status: stringField(data, "status"),
    updatedAt: dateField(data, "updatedAt") ?? dateField(data, "createdAt")
  };
}

export function mapPaymentRecord(id: string, data: DocumentData): PaymentRecord {
  const amount = data.grossAmountUSD;

  return {
    id,
    payerUserId: stringField(data, "payerUserId"),
    payeeUserId: stringField(data, "payeeUserId"),
    customerUserId: stringField(data, "customerUserId"),
    providerUserId: stringField(data, "providerUserId"),
    status: stringField(data, "status"),
    grossAmountUSD: typeof amount === "number" ? amount : null,
    updatedAt: dateField(data, "updatedAt") ?? dateField(data, "createdAt")
  };
}
